import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _createSuper from "@babel/runtime/helpers/esm/createSuper";

var _dec, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Heading } from '@instructure/ui-heading';
import { View } from '@instructure/ui-view';
import { themeable, ThemeablePropTypes } from '@instructure/ui-themeable';
import { omitProps, callRenderProp, getElementType } from '@instructure/ui-react-utils';
var styles = {
  componentId: 'ftPBL',
  template: function template(theme) {
    return "\n\n.ftPBL_bGBk{box-sizing:border-box;display:block;font-family:".concat(theme.fontFamily || 'inherit', ";margin-left:auto;margin-right:auto;position:relative;text-align:center}\n\n[dir=ltr] .ftPBL_bGBk,[dir=rtl] .ftPBL_bGBk{text-align:center}\n\n.ftPBL_bGBk:not(.ftPBL_bGiS){background-color:").concat(theme.backgroundColor || 'inherit', "}\n\n.ftPBL_bGBk.ftPBL_ywdX{cursor:not-allowed;opacity:0.5;pointer-events:none}\n\n.ftPBL_EwaR,.ftPBL_caGd,.ftPBL_eDnN,.ftPBL_cuDj,.ftPBL_elxg{display:block}\n\n.ftPBL_cuDj+.ftPBL_eDnN{margin:").concat(theme.largeMargin || 'inherit', " 0 0}\n\n.ftPBL_eDnN+.ftPBL_elxg,.ftPBL_cuDj+.ftPBL_elxg,.ftPBL_doqw .ftPBL_cuDj+.ftPBL_eDnN{margin:").concat(theme.mediumMargin || 'inherit', " 0 0}\n\n.ftPBL_doqw{padding:").concat(theme.paddingSmall || 'inherit', "}\n\n.ftPBL_doqw .ftPBL_cuDj{font-size:3rem}\n\n.ftPBL_doqw .ftPBL_elxg{font-size:").concat(theme.messageFontSizeSmall || 'inherit', "}\n\n.ftPBL_ycrn{padding:").concat(theme.paddingMedium || 'inherit', "}\n\n.ftPBL_ycrn .ftPBL_cuDj{font-size:5rem}\n\n.ftPBL_ycrn .ftPBL_elxg{font-size:").concat(theme.messageFontSizeMedium || 'inherit', "}\n\n.ftPBL_cMDj{padding:").concat(theme.paddingLarge || 'inherit', "}\n\n.ftPBL_cMDj .ftPBL_cuDj{font-size:10rem}\n\n.ftPBL_cMDj .ftPBL_elxg{font-size:").concat(theme.messageFontSizeLarge || 'inherit', "}\n\n.ftPBL_elxg{color:").concat(theme.messageColor || 'inherit', "}\n\n.ftPBL_cuDj{color:").concat(theme.iconColor || 'inherit', "}\n\n.ftPBL_cuDj>img,.ftPBL_cuDj>svg{display:block;margin:0 auto;max-width:100%}\n\n.ftPBL_cuDj>img{height:auto}\n\n.ftPBL_bGiS{-moz-appearance:none;-ms-user-select:none;-webkit-appearance:none;-webkit-user-select:none;appearance:none;background:").concat(theme.backgroundColor || 'inherit', ";border:").concat(theme.buttonBorderWidth || 'inherit', " ").concat(theme.buttonBorderStyle || 'inherit', " transparent;border-radius:").concat(theme.buttonBorderRadius || 'inherit', ";box-sizing:border-box;cursor:pointer;margin:0;text-decoration:none;touch-action:manipulation;user-select:none;width:100%}\n\n.ftPBL_bGiS:hover{border-style:").concat(theme.buttonHoverBorderStyle || 'inherit', "}\n\n.ftPBL_bGiS:focus,.ftPBL_bGiS:hover{border-color:").concat(theme.iconHoverColor || 'inherit', ";outline:none;text-decoration:none}\n\n.ftPBL_bGiS:focus .ftPBL_cuDj,.ftPBL_bGiS:hover .ftPBL_cuDj{color:").concat(theme.iconHoverColor || 'inherit', "}\n\n.ftPBL_bGiS:active{background:").concat(theme.clickableActiveBg || 'inherit', ";border-color:").concat(theme.iconHoverColor || 'inherit', "}\n\n.ftPBL_bGiS:active .ftPBL_cuDj,.ftPBL_bGiS:active .ftPBL_elxg{color:").concat(theme.clickableActiveText || 'inherit', "}\n\n.ftPBL_bGiS .ftPBL_elxg{color:").concat(theme.messageColorClickable || 'inherit', "}");
  },
  'root': 'ftPBL_bGBk',
  'clickable': 'ftPBL_bGiS',
  'disabled': 'ftPBL_ywdX',
  'button': 'ftPBL_EwaR',
  'content': 'ftPBL_caGd',
  'heading': 'ftPBL_eDnN',
  'hero': 'ftPBL_cuDj',
  'message': 'ftPBL_elxg',
  'small': 'ftPBL_doqw',
  'medium': 'ftPBL_ycrn',
  'large': 'ftPBL_cMDj'
};
import theme from "./theme.js";
/**
---
category: components
---
**/

var Billboard = (_dec = themeable(theme, styles), _dec(_class = (_temp = _class2 = /*#__PURE__*/function (_Component) {
  _inherits(Billboard, _Component);

  var _super = _createSuper(Billboard);

  function Billboard() {
    var _this;

    _classCallCheck(this, Billboard);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _this.handleClick = function (e) {
      var _this$props = _this.props,
          readOnly = _this$props.readOnly,
          onClick = _this$props.onClick;

      if (readOnly) {
        e.preventDefault();
        e.stopPropagation();
      } else if (typeof onClick === 'function') {
        onClick(e);
      }
    };

    return _this;
  }

  _createClass(Billboard, [{
    key: "renderHeading",
    value: function renderHeading() {
      var _this$props2 = this.props,
          headingLevel = _this$props2.headingLevel,
          headingAs = _this$props2.headingAs,
          heading = _this$props2.heading;
      return /*#__PURE__*/React.createElement("span", {
        className: styles.heading
      }, /*#__PURE__*/React.createElement(Heading, {
        level: headingLevel,
        as: headingAs,
        color: "primary"
      }, heading));
    }
  }, {
    key: "renderHero",
    value: function renderHero() {
      if (this.heroIsFunction) {
        return this.props.hero(this.SVGIconSize);
      } else {
        return this.props.hero;
      }
    }
  }, {
    key: "renderContent",
    value: function renderContent() {
      var _this$props3 = this.props,
          heading = _this$props3.heading,
          message = _this$props3.message,
          hero = _this$props3.hero;
      return /*#__PURE__*/React.createElement("span", {
        className: styles.content
      }, hero && /*#__PURE__*/React.createElement("span", {
        className: styles.hero
      }, this.renderHero()), heading && this.renderHeading(), message && /*#__PURE__*/React.createElement("span", {
        className: styles.message
      }, callRenderProp(message)));
    }
  }, {
    key: "render",
    value: function render() {
      var _classes;

      var _this$props4 = this.props,
          href = _this$props4.href,
          disabled = _this$props4.disabled,
          readOnly = _this$props4.readOnly,
          onClick = _this$props4.onClick,
          size = _this$props4.size,
          margin = _this$props4.margin,
          elementRef = _this$props4.elementRef;
      var classes = (_classes = {}, _defineProperty(_classes, styles.root, true), _defineProperty(_classes, styles[size], true), _defineProperty(_classes, styles.clickable, href || onClick), _defineProperty(_classes, styles.disabled, disabled), _classes);
      var Element = getElementType(Billboard, this.props);
      return /*#__PURE__*/React.createElement(View, {
        as: "div",
        margin: margin
      }, /*#__PURE__*/React.createElement(View, Object.assign({}, omitProps(this.props, _objectSpread({}, Billboard.propTypes, {}, View.propTypes)), {
        type: Element === 'button' ? 'button' : null,
        as: Element,
        elementRef: elementRef,
        className: classnames(classes),
        href: href,
        onClick: this.handleClick,
        disabled: disabled,
        "aria-disabled": disabled || readOnly ? 'true' : null
      }), this.renderContent()));
    }
  }, {
    key: "heroIsFunction",
    get: function get() {
      return typeof this.props.hero === 'function';
    }
  }, {
    key: "SVGIconSize",
    get: function get() {
      var size = this.props.size; // serve up appropriate SVGIcon size for each Billboard size

      if (size === 'small') {
        return 'medium';
      } else if (size === 'large') {
        return 'x-large';
      } else {
        return 'large';
      }
    }
  }]);

  Billboard.displayName = "Billboard";
  return Billboard;
}(Component), _class2.propTypes = {
  /**
   * Provide an <Img> component or Instructure Icon for the hero image
   */
  hero: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),

  /**
   * If you're using an icon, this prop will size it. Also sets the font-size
   * of the headline and message.
   */
  size: PropTypes.oneOf(['small', 'medium', 'large']),

  /**
   * the element type to render as
   */
  as: PropTypes.elementType,

  /**
   * provides a reference to the underlying html root element
   */
  elementRef: PropTypes.func,

  /**
   * The headline for the Billboard. Is styled as an h1 element by default
   */
  heading: PropTypes.string,

  /**
   * Choose the appropriately semantic tag for the heading
   */
  headingAs: PropTypes.oneOf(['h1', 'h2', 'h3', 'span']),

  /**
   * Choose the font-size for the heading (see the Heading component)
   */
  headingLevel: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4']),

  /**
   * Instructions or information for the Billboard. Note: you should not pass
   * interactive content to this prop if you are also providing an `href` or
   * `onClick`. That would cause the Billboard to render as a button or link
   * and would result in nested interactive content.
   */
  message: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),

  /**
   * If you add an onClick prop, the Billboard renders as a clickable button
   */
  onClick: PropTypes.func,

  /**
   * If `href` is provided, Billboard will render as a link
   */
  href: PropTypes.string,

  /**
   * Whether or not to disable the billboard
   */
  disabled: PropTypes.bool,

  /**
   * Works just like disabled but keeps the same styles as if it were active
   */
  readOnly: PropTypes.bool,

  /**
   * Valid values are `0`, `none`, `auto`, `xxx-small`, `xx-small`, `x-small`,
   * `small`, `medium`, `large`, `x-large`, `xx-large`. Apply these values via
   * familiar CSS-like shorthand. For example: `margin="small auto large"`.
   */
  margin: ThemeablePropTypes.spacing
}, _class2.defaultProps = {
  margin: void 0,
  disabled: false,
  readOnly: false,
  href: void 0,
  message: void 0,
  onClick: void 0,
  heading: void 0,
  hero: void 0,
  size: 'medium',
  headingAs: 'span',
  headingLevel: 'h1',
  as: 'span',
  elementRef: function elementRef(el) {}
}, _temp)) || _class);
export default Billboard;
export { Billboard };